import React, { useContext } from 'react';
import { Box, VStack, Spinner, Text, Heading } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import ThemeContext from '../context/ThemeContext';

const Loading = () => {
  const { themeData, businessName } = useContext(ThemeContext);

  // Floating animation (kept subtle)
  const float = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
  `;

  // Pulse glow animation
  const pulse = keyframes`
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  `;

  // Theme colors following Dashboard style
  const primaryColor = themeData?.colors?.primary || '#2B6CB0';
  // const secondaryColor = themeData?.colors?.secondary || '#2C5282';
  const accentColor = themeData?.colors?.accent || '#63B3ED';

  const isDarkTheme = themeData?.id === 'midnight';

  // Background — NO GRADIENT → consistent with Dashboard cards
  const backgroundColor = isDarkTheme ? '#0A0E17' : '#F7FAFC';

  // Card background (glassmorphic like dashboard)
  const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

  const headingColor = isDarkTheme ? accentColor : primaryColor;
  const spinnerColor = isDarkTheme ? accentColor : primaryColor;
  const spinnerEmptyColor = isDarkTheme ? 'whiteAlpha.200' : 'gray.200';
  const loadingTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';

  const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';

  const displayName = businessName || 'FinanSee';

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={backgroundColor}
      px={4}
      position="relative"
      overflow="hidden"
    >
      {/* Subtle grid pattern (same style as dashboard transparency) */}
      <Box
        position="absolute"
        inset="0"
        opacity={isDarkTheme ? 0.04 : 0.03}
        bgImage="radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)"
        bgSize="36px 36px"
        color={isDarkTheme ? 'whiteAlpha.300' : 'blackAlpha.300'}
        pointerEvents="none"
      />

      <VStack
        spacing={8}
        textAlign="center"
        bg={cardBg}
        p={12}
        borderRadius="2xl"
        boxShadow={isDarkTheme ? '0 8px 32px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.1)'}
        backdropFilter="blur(10px)"
        border="1px solid"
        borderColor={borderColor}
        position="relative"
      >
        {/* Animated Name */}
        <Heading
          as="h1"
          size="2xl"
          fontWeight="bold"
          color={headingColor}
          animation={`${float} 2.5s ease-in-out infinite`}
          letterSpacing="tight"
          textShadow={
            isDarkTheme
              ? `0 0 12px ${accentColor}60`
              : `0 1px 4px ${primaryColor}40`
          }
        >
          {displayName}
        </Heading>

        {/* Spinner with soft glow */}
        <Box position="relative">
          {isDarkTheme && (
            <Box
              position="absolute"
              inset="-12px"
              borderRadius="full"
              bg={`${accentColor}20`}
              animation={`${pulse} 2s ease-in-out infinite`}
              filter="blur(12px)"
            />
          )}

          <Spinner
            thickness="4px"
            speed="0.75s"
            emptyColor={spinnerEmptyColor}
            color={spinnerColor}
            size="xl"
          />
        </Box>

        {/* Loading Text */}
        <Text fontSize="lg" color={loadingTextColor} fontWeight="medium" maxW="300px">
          Preparing your personalized insights...
        </Text>
      </VStack>
    </Box>
  );
};

export default Loading;